import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Icon } from "./components/icons";
import {
  addDays,
  calendarDays,
  capitalise,
  cycleSummary,
  dateFromKey,
  differenceInDays,
  formatDate,
  localDateKey,
} from "./lib/cycle";
import {
  energyLevels,
  flowLevels,
  moodNames,
  symptomNames,
  type ConnectionDetails,
  type CycleDay,
  type CycleDayInput,
  type CycleRepository,
  type Energy,
  type Flow,
  type Mood,
  type Symptom,
} from "./lib/types";

type Page = "today" | "calendar" | "insights";

export default function App({
  repository,
  connection,
}: {
  repository: CycleRepository;
  connection?: ConnectionDetails;
}) {
  const [days, setDays] = useState<CycleDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState<Page>("today");
  const [logDate, setLogDate] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toast, setToast] = useState("");
  const today = localDateKey();

  useEffect(() => {
    let active = true;
    setLoading(true);
    repository
      .list()
      .then((value) => {
        if (active) setDays(value);
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : "Cycle notes could not be loaded.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [repository]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const recordsByDate = useMemo(
    () => new Map(days.map((day) => [day.date, day])),
    [days],
  );
  const summary = useMemo(() => cycleSummary(days, today), [days, today]);

  async function saveDay(input: CycleDayInput) {
    const saved = await repository.save(input, recordsByDate.get(input.date));
    setDays((current) =>
      [...current.filter((day) => day.date !== saved.date), saved].sort(
        (left, right) => left.date.localeCompare(right.date),
      ),
    );
    setLogDate(null);
    setToast(`Saved ${formatDate(saved.date, { weekday: "long", month: "short", day: "numeric" })}`);
  }

  return (
    <div className="app-shell">
      <DesktopRail page={page} onNavigate={setPage} />
      <div className="app-canvas">
        <Header
          page={page}
          onOpenSettings={() => setSettingsOpen(true)}
          connection={connection}
        />

        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => location.reload()} />
        ) : (
          <main className="page-frame">
            {page === "today" && (
              <TodayPage
                days={days}
                summary={summary}
                recordsByDate={recordsByDate}
                onLog={setLogDate}
              />
            )}
            {page === "calendar" && (
              <CalendarPage
                recordsByDate={recordsByDate}
                onLog={setLogDate}
              />
            )}
            {page === "insights" && (
              <InsightsPage days={days} summary={summary} />
            )}
          </main>
        )}

        <MobileNav page={page} onNavigate={setPage} />
      </div>

      {logDate && (
        <LogSheet
          date={logDate}
          existing={recordsByDate.get(logDate)}
          onClose={() => setLogDate(null)}
          onSave={saveDay}
        />
      )}
      {settingsOpen && (
        <SettingsSheet
          connection={connection}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {toast && (
        <div className="toast" role="status">
          <Icon name="check" />
          {toast}
        </div>
      )}
    </div>
  );
}

function Header({
  page,
  onOpenSettings,
  connection,
}: {
  page: Page;
  onOpenSettings(): void;
  connection?: ConnectionDetails;
}) {
  const titles: Record<Page, string> = {
    today: "Today",
    calendar: "Calendar",
    insights: "Patterns",
  };
  return (
    <header className="topbar">
      <a className="brand" href="/" aria-label="Tide home">
        <span className="brand-mark"><Icon name="wave" /></span>
        <span>Tide</span>
      </a>
      <span className="mobile-page-title">{titles[page]}</span>
      <button
        type="button"
        className="connection-button"
        onClick={onOpenSettings}
        aria-label="Open collection settings"
      >
        <span className="status-dot" />
        <span className="connection-copy">
          <strong>{connection ? connection.info.displayName : "Sample collection"}</strong>
          <small>
            {connection
              ? connection.info.route === "direct"
                ? "Connected directly"
                : "Connected through mdbase"
              : "Preview data"}
          </small>
        </span>
        <Icon name="settings" />
      </button>
    </header>
  );
}

function DesktopRail({
  page,
  onNavigate,
}: {
  page: Page;
  onNavigate(page: Page): void;
}) {
  return (
    <aside className="desktop-rail" aria-label="Primary navigation">
      <div className="rail-brand">
        <span className="brand-mark"><Icon name="wave" /></span>
        <span>
          <strong>Tide</strong>
          <small>Cycle notes</small>
        </span>
      </div>
      <nav>
        <NavButton icon="home" label="Today" value="today" page={page} onNavigate={onNavigate} />
        <NavButton icon="calendar" label="Calendar" value="calendar" page={page} onNavigate={onNavigate} />
        <NavButton icon="chart" label="Patterns" value="insights" page={page} onNavigate={onNavigate} />
      </nav>
      <p className="rail-note">
        <Icon name="lock" />
        Your notes stay in the mdbase collection you choose.
      </p>
    </aside>
  );
}

function MobileNav({
  page,
  onNavigate,
}: {
  page: Page;
  onNavigate(page: Page): void;
}) {
  return (
    <nav className="mobile-nav" aria-label="Primary navigation">
      <NavButton icon="home" label="Today" value="today" page={page} onNavigate={onNavigate} />
      <NavButton icon="calendar" label="Calendar" value="calendar" page={page} onNavigate={onNavigate} />
      <NavButton icon="chart" label="Patterns" value="insights" page={page} onNavigate={onNavigate} />
    </nav>
  );
}

function NavButton({
  icon,
  label,
  value,
  page,
  onNavigate,
}: {
  icon: "home" | "calendar" | "chart";
  label: string;
  value: Page;
  page: Page;
  onNavigate(page: Page): void;
}) {
  return (
    <button
      type="button"
      className={page === value ? "nav-button active" : "nav-button"}
      onClick={() => onNavigate(value)}
      aria-current={page === value ? "page" : undefined}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </button>
  );
}

function TodayPage({
  days,
  summary,
  recordsByDate,
  onLog,
}: {
  days: CycleDay[];
  summary: ReturnType<typeof cycleSummary>;
  recordsByDate: Map<string, CycleDay>;
  onLog(date: string): void;
}) {
  const today = localDateKey();
  const todayRecord = recordsByDate.get(today);
  const greeting =
    new Date().getHours() < 12
      ? "Good morning"
      : new Date().getHours() < 18
        ? "Good afternoon"
        : "Good evening";
  const recentWeek = Array.from({ length: 7 }, (_, index) =>
    addDays(today, index - 3),
  );
  return (
    <>
      <section className="today-intro">
        <div>
          <p className="eyebrow">{formatDate(today, { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1>{greeting}.</h1>
          <p className="lede">
            A quick check-in is enough. Leave the rest for another day.
          </p>
        </div>
        <button type="button" className="primary desktop-log" onClick={() => onLog(today)}>
          {todayRecord ? "Edit today" : "Log today"}
        </button>
      </section>

      <section className="week-strip" aria-label="This week">
        {recentWeek.map((date) => {
          const record = recordsByDate.get(date);
          const isToday = date === today;
          return (
            <button
              type="button"
              key={date}
              className={`week-day ${isToday ? "today" : ""} ${record ? `flow-${record.flow}` : ""}`}
              onClick={() => onLog(date)}
              aria-label={`${formatDate(date, { weekday: "long", month: "long", day: "numeric" })}${record ? `, ${record.flow} flow logged` : ", no entry"}`}
            >
              <small>{formatDate(date, { weekday: "narrow" })}</small>
              <strong>{dateFromKey(date).getDate()}</strong>
              <span className="week-marker" />
            </button>
          );
        })}
      </section>

      <div className="today-grid">
        <section className="cycle-panel" aria-labelledby="cycle-title">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Current rhythm</p>
              <h2 id="cycle-title">Your cycle at a glance</h2>
            </div>
            <Icon name="moon" />
          </div>
          <div className="cycle-content">
            <CycleDial
              cycleDay={summary.cycleDay}
              cycleLength={summary.usualLength ?? 28}
            />
            <div className="cycle-copy">
              {summary.nextPeriod ? (
                <>
                  <span className="estimate-label">
                    {summary.estimateKind === "personal" ? "Based on your recent cycles" : "Early estimate"}
                  </span>
                  <p>
                    Your next period may begin around{" "}
                    <strong>{formatDate(summary.nextPeriod, { month: "long", day: "numeric" })}</strong>.
                  </p>
                  <small>
                    Expect a few days either side. Estimates are for reflection, not contraception or diagnosis.
                  </small>
                </>
              ) : (
                <>
                  <span className="estimate-label">Learning your rhythm</span>
                  <p>Log the first days of your period to start seeing cycle timing.</p>
                  <small>Tide needs at least one period start before it can offer a rough estimate.</small>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="checkin-panel" aria-labelledby="checkin-title">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Daily check-in</p>
              <h2 id="checkin-title">{todayRecord ? "Today is noted" : "How are you feeling?"}</h2>
            </div>
            <Icon name="flower" />
          </div>
          {todayRecord ? (
            <div className="today-summary">
              <SummaryRow label="Flow" value={capitalise(todayRecord.flow)} dot={`flow-${todayRecord.flow}`} />
              <SummaryRow label="Energy" value={capitalise(todayRecord.energy)} />
              <SummaryRow
                label="Symptoms"
                value={todayRecord.symptoms.length ? todayRecord.symptoms.map(capitalise).join(", ") : "None noted"}
              />
              {todayRecord.notes && <blockquote>“{todayRecord.notes}”</blockquote>}
            </div>
          ) : (
            <div className="empty-checkin">
              <div className="empty-drops" aria-hidden="true">
                <Icon name="drop" />
                <Icon name="spark" />
                <Icon name="moon" />
              </div>
              <p>Flow, symptoms, mood and energy—only what feels useful today.</p>
            </div>
          )}
          <button type="button" className="primary wide" onClick={() => onLog(today)}>
            {todayRecord ? "Edit today’s note" : "Log today"}
          </button>
        </section>
      </div>

      <RecentNotes days={days} onLog={onLog} />
    </>
  );
}

function CycleDial({
  cycleDay,
  cycleLength,
}: {
  cycleDay: number | null;
  cycleLength: number;
}) {
  const count = Math.min(40, Math.max(21, cycleLength));
  const center = 120;
  const radius = 92;
  return (
    <div className="cycle-dial">
      <svg viewBox="0 0 240 240" role="img" aria-label={cycleDay ? `Cycle day ${cycleDay}` : "Cycle day unknown"}>
        <circle className="dial-guide" cx={center} cy={center} r={radius} />
        {Array.from({ length: count }, (_, index) => {
          const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
          const x = center + Math.cos(angle) * radius;
          const y = center + Math.sin(angle) * radius;
          const day = index + 1;
          const active = cycleDay === day;
          const past = cycleDay !== null && day < cycleDay;
          const period = day <= 5;
          return (
            <circle
              key={day}
              className={`dial-dot ${active ? "active" : ""} ${past ? "past" : ""} ${period ? "period" : ""}`}
              cx={x}
              cy={y}
              r={active ? 6.5 : period ? 4 : 3}
            />
          );
        })}
      </svg>
      <div className="dial-center">
        <small>{cycleDay ? "Cycle day" : "Cycle"}</small>
        <strong>{cycleDay ?? "—"}</strong>
        <span>{cycleDay ? `of ~${cycleLength}` : "Add a period"}</span>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  dot,
}: {
  label: string;
  value: string;
  dot?: string;
}) {
  return (
    <div className="summary-row">
      <span>{label}</span>
      <strong>
        {dot && <i className={`summary-dot ${dot}`} />}
        {value}
      </strong>
    </div>
  );
}

function RecentNotes({
  days,
  onLog,
}: {
  days: CycleDay[];
  onLog(date: string): void;
}) {
  const recent = [...days].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 4);
  return (
    <section className="recent-section" aria-labelledby="recent-title">
      <div className="section-title-row">
        <div>
          <p className="eyebrow">Recent notes</p>
          <h2 id="recent-title">The last few check-ins</h2>
        </div>
        <span>{days.length} total</span>
      </div>
      {recent.length ? (
        <div className="recent-list">
          {recent.map((day) => (
            <button type="button" className="recent-row" key={day.path} onClick={() => onLog(day.date)}>
              <span className={`date-tile flow-${day.flow}`}>
                <small>{formatDate(day.date, { month: "short" })}</small>
                <strong>{dateFromKey(day.date).getDate()}</strong>
              </span>
              <span className="recent-main">
                <strong>{capitalise(day.flow)} flow</strong>
                <small>
                  {day.symptoms.length
                    ? day.symptoms.map(capitalise).join(" · ")
                    : `${capitalise(day.mood)} mood · ${capitalise(day.energy)} energy`}
                </small>
              </span>
              <span className="pain-mark">{day.pain ? `Pain ${day.pain}/4` : "No pain"}</span>
              <Icon name="chevron-right" />
            </button>
          ))}
        </div>
      ) : (
        <p className="plain-empty">No entries yet. Log today when you’re ready.</p>
      )}
    </section>
  );
}

function CalendarPage({
  recordsByDate,
  onLog,
}: {
  recordsByDate: Map<string, CycleDay>;
  onLog(date: string): void;
}) {
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1, 12),
  );
  const [selected, setSelected] = useState(localDateKey());
  const grid = calendarDays(month);
  const selectedRecord = recordsByDate.get(selected);
  const today = localDateKey();
  function moveMonth(offset: number) {
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1, 12));
  }
  return (
    <>
      <section className="page-intro">
        <p className="eyebrow">Cycle calendar</p>
        <h1>See the month, not a verdict.</h1>
        <p>Logged days are facts. Future dates are gentle estimates and can shift.</p>
      </section>
      <section className="calendar-panel">
        <div className="calendar-toolbar">
          <button type="button" className="icon-button" onClick={() => moveMonth(-1)} aria-label="Previous month">
            <Icon name="chevron-left" />
          </button>
          <h2>{new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric" }).format(month)}</h2>
          <button type="button" className="icon-button" onClick={() => moveMonth(1)} aria-label="Next month">
            <Icon name="chevron-right" />
          </button>
        </div>
        <div className="weekday-row" aria-hidden="true">
          {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
        </div>
        <div className="calendar-grid">
          {grid.map((item) => {
            const record = recordsByDate.get(item.key);
            return (
              <button
                type="button"
                key={item.key}
                className={`calendar-day ${item.inMonth ? "" : "outside"} ${selected === item.key ? "selected" : ""} ${today === item.key ? "is-today" : ""} ${record ? `has-record flow-${record.flow}` : ""}`}
                onClick={() => setSelected(item.key)}
                aria-label={`${formatDate(item.key, { weekday: "long", month: "long", day: "numeric" })}${record ? `, ${record.flow} flow` : ""}`}
              >
                <span>{item.day}</span>
                {record && <i />}
              </button>
            );
          })}
        </div>
        <div className="calendar-legend">
          <span><i className="flow-heavy" /> Medium or heavy</span>
          <span><i className="flow-light" /> Light or spotting</span>
          <span><i className="flow-none" /> Check-in</span>
        </div>
      </section>
      <section className="selected-day-panel">
        <div>
          <p className="eyebrow">Selected day</p>
          <h2>{formatDate(selected, { weekday: "long", month: "long", day: "numeric" })}</h2>
        </div>
        {selectedRecord ? (
          <div className="selected-day-details">
            <SummaryRow label="Flow" value={capitalise(selectedRecord.flow)} dot={`flow-${selectedRecord.flow}`} />
            <SummaryRow label="Mood" value={capitalise(selectedRecord.mood)} />
            <SummaryRow label="Energy" value={capitalise(selectedRecord.energy)} />
            <SummaryRow label="Pain" value={`${selectedRecord.pain} / 4`} />
          </div>
        ) : (
          <p className="selected-empty">Nothing recorded for this day.</p>
        )}
        <button type="button" className={selectedRecord ? "secondary" : "primary"} onClick={() => onLog(selected)}>
          {selectedRecord ? "Edit this day" : "Log this day"}
        </button>
      </section>
    </>
  );
}

function InsightsPage({
  days,
  summary,
}: {
  days: CycleDay[];
  summary: ReturnType<typeof cycleSummary>;
}) {
  const symptomCounts = symptomNames
    .map((symptom) => ({
      symptom,
      count: days.filter((day) => day.symptoms.includes(symptom)).length,
    }))
    .filter((item) => item.count)
    .sort((left, right) => right.count - left.count);
  const maxSymptom = symptomCounts[0]?.count ?? 1;
  const cycleLengths = summary.recentCycleLengths;
  const maxLength = Math.max(35, ...cycleLengths);
  const bleedingDays = days.filter((day) => day.flow !== "none" && day.flow !== "spotting").length;
  const averagePain =
    days.length
      ? (days.reduce((sum, day) => sum + day.pain, 0) / days.length).toFixed(1)
      : "—";
  return (
    <>
      <section className="page-intro">
        <p className="eyebrow">Patterns</p>
        <h1>Your body, in your own words.</h1>
        <p>Small observations become more useful over time. They never become a diagnosis.</p>
      </section>
      <section className="metric-strip">
        <div>
          <small>Usual cycle</small>
          <strong>{summary.usualLength ? `${summary.usualLength} days` : "Learning"}</strong>
        </div>
        <div>
          <small>Days logged</small>
          <strong>{days.length}</strong>
        </div>
        <div>
          <small>Period days</small>
          <strong>{bleedingDays}</strong>
        </div>
        <div>
          <small>Average pain</small>
          <strong>{averagePain}{averagePain !== "—" && " / 4"}</strong>
        </div>
      </section>
      <div className="insights-grid">
        <section className="insight-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Cycle length</p>
              <h2>Recent rhythm</h2>
            </div>
            <Icon name="wave" />
          </div>
          {cycleLengths.length ? (
            <div className="cycle-bars">
              {cycleLengths.map((length, index) => (
                <div className="cycle-bar-column" key={`${length}-${index}`}>
                  <span>{length}</span>
                  <div className="cycle-bar-track">
                    <i style={{ height: `${Math.max(18, (length / maxLength) * 100)}%` }} />
                  </div>
                  <small>{index === cycleLengths.length - 1 ? "Latest" : `${cycleLengths.length - index} ago`}</small>
                </div>
              ))}
            </div>
          ) : (
            <p className="plain-empty">Two logged period starts will reveal your first cycle length.</p>
          )}
        </section>
        <section className="insight-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Symptoms</p>
              <h2>What comes up most</h2>
            </div>
            <Icon name="flower" />
          </div>
          {symptomCounts.length ? (
            <div className="symptom-bars">
              {symptomCounts.slice(0, 5).map(({ symptom, count }) => (
                <div className="symptom-bar" key={symptom}>
                  <span>{capitalise(symptom)}</span>
                  <div><i style={{ width: `${(count / maxSymptom) * 100}%` }} /></div>
                  <strong>{count}</strong>
                </div>
              ))}
            </div>
          ) : (
            <p className="plain-empty">Symptoms you log will be summarised here.</p>
          )}
        </section>
      </div>
      <section className="privacy-note">
        <Icon name="lock" />
        <div>
          <strong>These patterns are calculated in your browser.</strong>
          <p>Tide uses the cycle-day contract for every record operation and does not send your entries to an app database.</p>
        </div>
      </section>
    </>
  );
}

function LogSheet({
  date,
  existing,
  onClose,
  onSave,
}: {
  date: string;
  existing?: CycleDay;
  onClose(): void;
  onSave(input: CycleDayInput): Promise<void>;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const [flow, setFlow] = useState<Flow>(existing?.flow ?? "none");
  const [symptoms, setSymptoms] = useState<Symptom[]>(existing?.symptoms ?? []);
  const [pain, setPain] = useState(existing?.pain ?? 0);
  const [mood, setMood] = useState<Mood>(existing?.mood ?? "steady");
  const [energy, setEnergy] = useState<Energy>(existing?.energy ?? "medium");
  const [sleep, setSleep] = useState(existing?.sleep_quality ?? 3);
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingRecovery, setPendingRecovery] = useState(false);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    document.body.classList.add("sheet-open");
    return () => {
      document.body.classList.remove("sheet-open");
      previous?.focus();
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onSave({
        date,
        flow,
        symptoms,
        pain,
        mood,
        energy,
        sleep_quality: sleep,
        notes: notes.trim(),
      });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "This check-in could not be saved.");
      if (
        reason instanceof Error &&
        (reason as Error & { code?: string }).code === "operation_outcome_unknown"
      ) {
        setPendingRecovery(true);
      }
      setSaving(false);
    }
  }

  function toggleSymptom(symptom: Symptom) {
    setSymptoms((current) =>
      current.includes(symptom)
        ? current.filter((item) => item !== symptom)
        : [...current, symptom],
    );
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="log-title" ref={dialog} tabIndex={-1}>
        <header className="sheet-header">
          <div>
            <p className="eyebrow">{existing ? "Edit check-in" : "New check-in"}</p>
            <h2 id="log-title">{formatDate(date, { weekday: "long", month: "long", day: "numeric" })}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </header>
        <form onSubmit={submit}>
          <fieldset>
            <legend>Flow</legend>
            <div className="flow-options">
              {flowLevels.map((value) => (
                <label key={value} className={`flow-option flow-${value}`}>
                  <input type="radio" name="flow" value={value} checked={flow === value} onChange={() => setFlow(value)} />
                  <span className="flow-drop"><Icon name="drop" /></span>
                  <strong>{capitalise(value)}</strong>
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Symptoms</legend>
            <div className="chip-options">
              {symptomNames.map((symptom) => (
                <label key={symptom} className={symptoms.includes(symptom) ? "chip checked" : "chip"}>
                  <input type="checkbox" checked={symptoms.includes(symptom)} onChange={() => toggleSymptom(symptom)} />
                  {capitalise(symptom)}
                  {symptoms.includes(symptom) && <Icon name="check" />}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="form-pair">
            <fieldset>
              <legend>Pain</legend>
              <div className="scale-options">
                {[0, 1, 2, 3, 4].map((value) => (
                  <label key={value}>
                    <input type="radio" name="pain" checked={pain === value} onChange={() => setPain(value)} />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
              <div className="scale-labels"><span>None</span><span>Severe</span></div>
            </fieldset>
            <fieldset>
              <legend>Sleep</legend>
              <div className="scale-options">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label key={value}>
                    <input type="radio" name="sleep" checked={sleep === value} onChange={() => setSleep(value)} />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
              <div className="scale-labels"><span>Poor</span><span>Great</span></div>
            </fieldset>
          </div>
          <div className="form-pair">
            <label className="select-field">
              <span>Mood</span>
              <select value={mood} onChange={(event) => setMood(event.target.value as Mood)}>
                {moodNames.map((value) => <option key={value} value={value}>{capitalise(value)}</option>)}
              </select>
            </label>
            <label className="select-field">
              <span>Energy</span>
              <select value={energy} onChange={(event) => setEnergy(event.target.value as Energy)}>
                {energyLevels.map((value) => <option key={value} value={value}>{capitalise(value)}</option>)}
              </select>
            </label>
          </div>
          <label className="notes-field">
            <span>Private notes <small>Optional</small></span>
            <textarea
              value={notes}
              maxLength={4000}
              rows={3}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Anything you want to remember about today…"
            />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="sheet-actions">
            <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            {pendingRecovery ? (
              <button type="button" className="primary" onClick={() => window.location.reload()}>
                Reload to recover save
              </button>
            ) : (
              <button type="submit" className="primary" disabled={saving}>
                {saving ? "Saving…" : existing ? "Save changes" : "Save check-in"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function SettingsSheet({
  connection,
  onClose,
}: {
  connection?: ConnectionDetails;
  onClose(): void;
}) {
  const [working, setWorking] = useState(false);
  const [status, setStatus] = useState("");
  async function direct(action: "check" | "request") {
    if (!connection) return;
    setWorking(true);
    setStatus("");
    try {
      if (action === "check") await connection.checkDirectAccess();
      else await connection.requestDirectAccess();
      setStatus("Connection route updated.");
    } catch (reason) {
      setStatus(reason instanceof Error ? reason.message : "The route could not be updated.");
    } finally {
      setWorking(false);
    }
  }
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="settings-sheet" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <header className="sheet-header">
          <div>
            <p className="eyebrow">Data & connection</p>
            <h2 id="settings-title">Your collection</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </header>
        <div className="settings-body">
          <div className="collection-identity">
            <span className="collection-mark"><Icon name="note" /></span>
            <div>
              <strong>{connection?.info.displayName ?? "Sample collection"}</strong>
              <small>{connection?.info.collectionId ?? "Demo mode — no records are written"}</small>
            </div>
            <span className="status-label"><i /> Connected</span>
          </div>
          <div className="settings-rows">
            <div className="settings-row">
              <span>Route</span>
              <strong>
                {connection
                  ? connection.info.route === "direct"
                    ? "Direct to this computer"
                    : connection.info.route === "remote"
                      ? "Hosted collection"
                      : "Encrypted mdbase relay"
                  : "In-memory preview"}
              </strong>
            </div>
            <div className="settings-row">
              <span>Access</span>
              <strong>Collection setup + cycle records</strong>
            </div>
            <div className="settings-row">
              <span>Format</span>
              <strong>Markdown + YAML</strong>
            </div>
          </div>
          <div className="settings-explainer">
            <Icon name="lock" />
            <p>
              mdbase Connect reviews collection-wide setup for Tide's definitions. Tide's record operations always use the cycle-day contract selector; you can review or revoke the grant in Connect.
            </p>
          </div>
          {connection && (
            <>
              {status && <p className="settings-status" role="status">{status}</p>}
              <div className="settings-actions">
                <button type="button" className="secondary" disabled={working} onClick={() => void direct(connection.info.directAccess === "permission_required" ? "request" : "check")}>
                  {connection.info.directAccess === "permission_required" ? "Allow direct access" : "Check direct access"}
                </button>
                <button type="button" className="secondary" onClick={connection.switchCollection}>Choose another collection</button>
                <button type="button" className="quiet-danger" onClick={() => {
                  if (window.confirm("Forget this collection on this browser? Your Markdown records will not be deleted.")) {
                    connection.forgetCollection();
                  }
                }}>
                  Forget this connection
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <main className="state-page" aria-live="polite">
      <span className="loading-mark"><Icon name="wave" /></span>
      <h1>Opening your notes…</h1>
      <p>Reading cycle-day records from your collection.</p>
    </main>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry(): void }) {
  return (
    <main className="state-page">
      <span className="error-mark">!</span>
      <h1>Your notes could not be opened.</h1>
      <p>{message}</p>
      <button type="button" className="primary" onClick={onRetry}>Try again</button>
    </main>
  );
}
